/**
 * Compact input template (FEAT-2026-0005, Phase 2 polish).
 *
 * Replaces RJSF's default MUI TextField (which uses normal density and a
 * full-line helper-text reservation) with a `size="small"` TextField that
 * renders in ~36 px instead of ~56 px and only shows helper text when it
 * actually has content.
 */

import { TextField } from '@mui/material';
import type { BaseInputTemplateProps } from '@rjsf/utils';

export const BaseInputTemplate = (props: BaseInputTemplateProps) => {
  const {
    id, value, type, schema, options, required, disabled, readonly,
    autofocus, onChange, onBlur, onFocus, rawErrors,
  } = props;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    if (v === '' && !required) {
      onChange(undefined);
      return;
    }
    if (type === 'number' || schema.type === 'number' || schema.type === 'integer') {
      const n = Number(v);
      onChange(Number.isFinite(n) ? n : v);
      return;
    }
    onChange(v);
  };

  const inputType = options?.inputType ??
    (schema.type === 'integer' || schema.type === 'number' ? 'number' : 'text');

  return (
    <TextField
      id={id}
      type={inputType}
      value={value ?? ''}
      required={required}
      disabled={disabled || readonly}
      autoFocus={autofocus}
      size="small"
      fullWidth
      error={!!rawErrors?.length}
      onChange={handleChange}
      onBlur={onBlur && ((e) => onBlur(id, e.target.value))}
      onFocus={onFocus && ((e) => onFocus(id, e.target.value))}
      InputProps={{ sx: { fontSize: '0.85rem', py: 0 } }}
      inputProps={{
        // For numbers, allow finer step to match the schema's typical units.
        step: schema.type === 'integer' ? 1 : 'any',
      }}
    />
  );
};
